import React, { useState, useEffect } from 'react'
import { Modal, Button, Icon, message, Input } from 'antd'
import { useWardrobe } from '../../../../hooks/useWardrobe'
import { useOutfitLibrary } from './shared'
import SelectedOutfitCard from './SelectedOutfitCard'
import WardrobeLibraryPickerModal from './WardrobeLibraryPickerModal'
import CreateOutfitModal from './CreateOutfitModal'
import OutfitInUseModal from './OutfitInUseModal'
import './WardrobeSelectorModal.scss'

/**
 * Modal for selecting/mapping outfits to character placeholders
 *
 * @param {Object} props
 * @param {Boolean} props.visible - Modal visibility
 * @param {Function} props.onClose - Close handler
 * @param {String} props.characterName - Character display name
 * @param {String} props.characterKey - Character placeholder key
 * @param {Object} props.wardrobe - Character wardrobe mapping
 * @param {Object} props.outfitMap - Outfit descriptions by key
 * @param {Object} props.outfitsReference - Current outfit tags by character
 * @param {Function} props.onSave - Save handler (characterKey, outfitTags) => void
 * @param {Object} props.formData - Form data containing outfits
 * @param {Function} props.setFormData - Function to update form data
 */
const WardrobeSelectorModal = ({
  visible,
  onClose,
  characterName,
  characterKey,
  wardrobe = {},
  outfitMap = {},
  outfitsReference = {},
  onSave,
  formData = {},
  setFormData = () => {},
  videoId,
  initialAction
}) => {
  const [localWardrobe, setLocalWardrobe] = useState({})
  const [localOutfitTags, setLocalOutfitTags] = useState({})
  const [isPickerVisible, setIsPickerVisible] = useState(false)
  const [placeholderToSelect, setPlaceholderToSelect] = useState(null)
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false)
  const [outfitKeyToCreate, setOutfitKeyToCreate] = useState(null)
  const [pickerMode, setPickerMode] = useState('select')

  const [isCustomOutfitModalVisible, setIsCustomOutfitModalVisible] = useState(
    false
  )
  const [customOutfitPrompt, setCustomOutfitPrompt] = useState('')
  const [editingOutfitKey, setEditingOutfitKey] = useState(null)
  const [inUseModalVisible, setInUseModalVisible] = useState(false)
  const [outfitKeyForInUse, setOutfitKeyForInUse] = useState(null)

  const {
    createOutfit,
    isCreatingOutfit,
    creationProgress,
    stopPolling
  } = useWardrobe({ videoId })

  const { outfits: libraryOutfits, fetchIfNeeded } = useOutfitLibrary({
    formData,
    setFormData,
    videoId
  })

  const outfitKeys = wardrobe[characterKey] || []

  // Handler for when SelectedOutfitCard resolves an image from a tag
  const handleImageResolved = (outfitKey, imageUrl) => {
    setLocalWardrobe(prev => ({
      ...prev,
      [outfitKey]: imageUrl
    }))
  }

  // Push API-fetched outfit into formData.outfits so picker can find it
  const handleOutfitFetched = outfit => {
    if (!outfit) return
    setFormData(prev => {
      const existing = prev.outfits || []
      const alreadyExists = existing.some(
        o => (o.tag || o.id) === (outfit.tag || outfit.id)
      )
      if (alreadyExists) return prev
      return { ...prev, outfits: [...existing, outfit] }
    })
  }

  useEffect(() => {
    if (visible && characterKey) {
      const currentTags = outfitsReference[characterKey] || {}
      setLocalOutfitTags(currentTags)

      // Reset localWardrobe — SelectedOutfitCard will fetch images per-tag
      setLocalWardrobe({})

      setPlaceholderToSelect(null)
      setOutfitKeyToCreate(null)

      // Use centralized hook - only fetches if formData.outfits is empty
      fetchIfNeeded()

      if (initialAction === 'addCustomOutfit') {
        handleOpenAddCustomOutfit()
      }
    }
    return () => stopPolling()
  }, [visible, characterKey, outfitsReference, initialAction])

  const handleLibrarySelect = (imageUrl, tag) => {
    if (placeholderToSelect) {
      setLocalWardrobe(prev => ({
        ...prev,
        [placeholderToSelect]: imageUrl
      }))
      setLocalOutfitTags(prev => ({
        ...prev,
        [placeholderToSelect]: tag
      }))
      message.success(
        `Applied outfit to ${placeholderToSelect.replace(/^_{2}|_{2}$/g, '')}`
      )
      setIsPickerVisible(false)
      setPlaceholderToSelect(null)
    }
  }

  const handleQuickSelect = (outfitKey, outfit) => {
    if (outfit) {
      const url =
        outfit.primary_image || (outfit.views && Object.values(outfit.views)[0])
      setLocalWardrobe(prev => ({
        ...prev,
        [outfitKey]: url
      }))
      setLocalOutfitTags(prev => ({
        ...prev,
        [outfitKey]: outfit.tag || outfit.id
      }))
    }
  }

  const handleOpenCreateModal = outfitKey => {
    setOutfitKeyToCreate(outfitKey)
    setIsCreateModalVisible(true)
  }

  const handleCreateSuccess = outfitData => {
    if (outfitKeyToCreate && outfitData) {
      const outfitImage =
        outfitData.views?.front ||
        outfitData.views?.[outfitData.primary_view] ||
        (outfitData.views && Object.values(outfitData.views)[0]) ||
        outfitData.metadata?.attributes?.primary_image

      if (outfitImage) {
        setLocalWardrobe(prev => ({
          ...prev,
          [outfitKeyToCreate]: outfitImage
        }))
        setLocalOutfitTags(prev => ({
          ...prev,
          [outfitKeyToCreate]: outfitData.tag || outfitData.id
        }))
      }
    }
    setOutfitKeyToCreate(null)
  }

  const handleSave = () => {
    if (onSave && characterKey) {
      onSave(characterKey, localOutfitTags)
      message.success(`Wardrobe saved for ${characterName}`)
      onClose()
    }
  }

  const handleOpenAddCustomOutfit = () => {
    setEditingOutfitKey(null)
    setCustomOutfitPrompt('')
    setIsCustomOutfitModalVisible(true)
  }

  const handleOpenEditCustomOutfit = (outfitKey, currentPrompt) => {
    setEditingOutfitKey(outfitKey)
    setCustomOutfitPrompt(currentPrompt)
    setIsCustomOutfitModalVisible(true)
  }

  const handleSaveCustomOutfit = () => {
    if (!customOutfitPrompt.trim()) {
      message.error('Prompt cannot be empty')
      return
    }

    setFormData(prev => {
      const newOutfitMap = { ...(prev.outfitMap || {}) }
      let newWardrobe = { ...(prev.wardrobe || {}) }

      if (editingOutfitKey) {
        newOutfitMap[editingOutfitKey] = customOutfitPrompt.trim()
      } else {
        const existingKeys = Object.keys(newOutfitMap)
        let maxIndex = 0
        existingKeys.forEach(key => {
          const match = key.match(/__outfit(\d+)__/)
          if (match) {
            const index = parseInt(match[1], 10)
            if (index > maxIndex) maxIndex = index
          }
        })
        const newKey = `__outfit${maxIndex + 1}__`
        newOutfitMap[newKey] = customOutfitPrompt.trim()

        const charWardrobe = newWardrobe[characterKey] || []
        newWardrobe[characterKey] = [...charWardrobe, newKey]
      }

      return {
        ...prev,
        outfitMap: newOutfitMap,
        wardrobe: newWardrobe
      }
    })

    setIsCustomOutfitModalVisible(false)
    setEditingOutfitKey(null)
    setCustomOutfitPrompt('')
    message.success(editingOutfitKey ? 'Outfit updated' : 'Custom outfit added')
  }

  const handleDeleteOutfit = outfitKey => {
    // Check if outfit is used in shots
    const { shotList = [] } = formData
    let isUsed = false

    shotList.forEach(scene => {
      if (scene.shots) {
        scene.shots.forEach(shot => {
          if (
            (shot.initial_image_prompt &&
              shot.initial_image_prompt.includes(outfitKey)) ||
            (shot.video_prompt && shot.video_prompt.includes(outfitKey))
          ) {
            isUsed = true
          }
        })
      }
    })

    if (isUsed) {
      setOutfitKeyForInUse(outfitKey)
      setInUseModalVisible(true)
      return
    }

    Modal.confirm({
      title: 'Delete Outfit',
      content: 'Are you sure you want to delete this custom outfit?',
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'No',
      centered: true,
      onOk: () => {
        // Safely remove outfit
        setFormData(prev => {
          const newOutfitMap = { ...(prev.outfitMap || {}) }
          delete newOutfitMap[outfitKey]

          const newWardrobe = { ...(prev.wardrobe || {}) }
          if (newWardrobe[characterKey]) {
            newWardrobe[characterKey] = newWardrobe[characterKey].filter(
              k => k !== outfitKey
            )
          }

          const newOutfitsReference = { ...(prev.outfits_reference || {}) }
          if (newOutfitsReference[characterKey]) {
            delete newOutfitsReference[characterKey][outfitKey]
          }

          return {
            ...prev,
            outfitMap: newOutfitMap,
            wardrobe: newWardrobe,
            outfits_reference: newOutfitsReference
          }
        })

        setLocalOutfitTags(prev => {
          const next = { ...prev }
          delete next[outfitKey]
          return next
        })

        message.success('Outfit deleted')
      }
    })
  }

  const performDeleteOutfit = outfitKey => {
    // Safely remove outfit
    setFormData(prev => {
      const newOutfitMap = { ...(prev.outfitMap || {}) }
      delete newOutfitMap[outfitKey]

      const newWardrobe = { ...(prev.wardrobe || {}) }
      if (newWardrobe[characterKey]) {
        newWardrobe[characterKey] = newWardrobe[characterKey].filter(
          k => k !== outfitKey
        )
      }

      const newOutfitsReference = { ...(prev.outfits_reference || {}) }
      if (newOutfitsReference[characterKey]) {
        delete newOutfitsReference[characterKey][outfitKey]
      }

      return {
        ...prev,
        outfitMap: newOutfitMap,
        wardrobe: newWardrobe,
        outfits_reference: newOutfitsReference
      }
    })

    setLocalOutfitTags(prev => {
      const next = { ...prev }
      delete next[outfitKey]
      return next
    })

    message.success('Outfit deleted')
  }

  const getOutfitDescription = outfitKey => {
    return outfitMap[outfitKey] || outfitKey
  }

  const openPicker = (outfitKey, mode = 'select') => {
    setPlaceholderToSelect(outfitKey)
    setPickerMode(mode)
    setIsPickerVisible(true)
  }

  const getInitialOutfitForPicker = () => {
    if (pickerMode !== 'view' || !placeholderToSelect) return null

    const currentImg = localWardrobe[placeholderToSelect]
    const currentTag = localOutfitTags[placeholderToSelect]

    // Try to find in the loaded library outfits first
    if (currentTag) {
      const found = libraryOutfits.find(o => (o.tag || o.id) === currentTag)
      if (found) return found
    }

    if (currentImg) {
      return libraryOutfits.find(
        o =>
          o.primary_image === currentImg ||
          (o.views && Object.values(o.views).includes(currentImg))
      )
    }

    // If not found locally, the picker will handle fetching
    return null
  }

  const renderOutfitCard = outfitKey => {
    const description = getOutfitDescription(outfitKey)
    const tag = localOutfitTags[outfitKey]
    const image = localWardrobe[outfitKey]

    return (
      <SelectedOutfitCard
        key={outfitKey}
        outfitKey={outfitKey}
        tag={tag}
        description={description}
        initialImage={image}
        libraryOutfits={libraryOutfits}
        onImageResolved={handleImageResolved}
        onOutfitFetched={handleOutfitFetched}
        onQuickSelect={handleQuickSelect}
        onOpenPicker={openPicker}
        onOpenCreate={handleOpenCreateModal}
        onEditOutfit={handleOpenEditCustomOutfit}
        onDeleteOutfit={handleDeleteOutfit}
        videoId={videoId}
      />
    )
  }

  return (
    <>
      <Modal
        maskClosable={true}
        visible={visible}
        onCancel={onClose}
        footer={[
          <Button key="cancel" onClick={onClose} className="footer-cancel-btn">
            Cancel
          </Button>,
          <Button
            key="save"
            type="primary"
            onClick={handleSave}
            className="footer-save-btn"
          >
            Save Selection
          </Button>
        ]}
        width={1000}
        centered
        className="wardrobe-modal"
        title={null}
        closable={false}
      >
        <div className="modal-title-bar">
          <h2
            className="modal-title"
            style={{ display: 'flex', alignItems: 'center', gap: '16px' }}
          >
            <span>Wardrobe: {characterName || 'Unknown Character'}</span>
            <Button
              size="small"
              icon="plus"
              type="primary"
              ghost
              onClick={handleOpenAddCustomOutfit}
            >
              Add Custom Outfit
            </Button>
          </h2>
          <div className="close-icon" onClick={onClose}>
            <Icon type="close" />
          </div>
        </div>

        <div className="wardrobe-list-container">
          {outfitKeys.length > 0 ? (
            <div className="wardrobe-outfits-list">
              <div className="wardrobe-header">
                <div className="header-text">
                  <h3 className="header-title">Required Outfits</h3>
                  <p className="header-hint">
                    Provide references for the outfits extracted from the
                    script.
                  </p>
                </div>
              </div>

              <div className="outfits-grid">
                {outfitKeys.map(renderOutfitCard)}
              </div>
            </div>
          ) : (
            <div className="empty-state">
              <Icon type="skin" theme="twoTone" twoToneColor="#5057d5" />
              <p>No outfits are currently required for this character.</p>
              <Button
                type="primary"
                icon="plus"
                style={{ marginTop: '16px' }}
                onClick={handleOpenAddCustomOutfit}
              >
                Add Custom Outfit
              </Button>
            </div>
          )}
        </div>
      </Modal>

      <WardrobeLibraryPickerModal
        visible={isPickerVisible}
        onClose={() => setIsPickerVisible(false)}
        onSelect={handleLibrarySelect}
        targetPlaceholder={placeholderToSelect}
        mode={pickerMode}
        initialOutfit={getInitialOutfitForPicker()}
        formData={formData}
        setFormData={setFormData}
        videoId={videoId}
      />

      <CreateOutfitModal
        visible={isCreateModalVisible}
        onClose={() => {
          setIsCreateModalVisible(false)
          setOutfitKeyToCreate(null)
        }}
        onSuccess={handleCreateSuccess}
        outfitDescription={
          outfitKeyToCreate ? getOutfitDescription(outfitKeyToCreate) : ''
        }
        outfitLabel={outfitKeyToCreate?.replace(/^_{2}|_{2}$/g, '')}
        createOutfit={createOutfit}
        isCreating={isCreatingOutfit}
        creationProgress={creationProgress}
      />

      <Modal
        maskClosable={false}
        visible={isCustomOutfitModalVisible}
        title={editingOutfitKey ? 'Edit Custom Outfit' : 'Add Custom Outfit'}
        onCancel={() => setIsCustomOutfitModalVisible(false)}
        onOk={handleSaveCustomOutfit}
        okText="Save"
        centered
        destroyOnClose
      >
        <div style={{ marginBottom: 8 }}>Outfit Prompt:</div>
        <Input.TextArea
          rows={4}
          placeholder="Enter a description for this outfit (e.g. 'Casual dinner: Navy blue polo...')"
          value={customOutfitPrompt}
          onChange={e => setCustomOutfitPrompt(e.target.value)}
        />
      </Modal>

      <OutfitInUseModal
        visible={inUseModalVisible}
        onClose={() => {
          setInUseModalVisible(false)
          setOutfitKeyForInUse(null)
        }}
        outfitKey={outfitKeyForInUse}
        formData={formData}
        setFormData={setFormData}
        onDeleteOutfit={performDeleteOutfit}
      />
    </>
  )
}

export default WardrobeSelectorModal
